import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import Api from "@ciq/shared/api/api"
import { useAppContext } from "@ciq/shared/hooks/context"
import { useAnalyticsContext } from "@ciq/shared/contexts/AnalyticsContext"
import { FeatureFlags } from "@ciq/shared/constants/featureFlags"

/**
 * @typedef {Object} DnaAuthParams
 * @property {string|null} clientId - Cisco client ID
 * @property {string|null} redirectUri - Cisco redirect URI
 * @property {string|null} state - OAuth state parameter
 */

/** Expected client_id for CampusIQ's DNA Spaces integration. */
export const DNA_SPACES_CLIENT_ID = "degree_analytics_dna"

/** Allowed redirect URI hosts for Cisco Spaces OAuth callbacks. */
const ALLOWED_REDIRECT_HOSTS = ["dnaspaces.io", "dnaspaces.eu", "cisco.com"]

/**
 * Parses DNA Spaces auth query parameters.
 *
 * @param {URLSearchParams} params - URL search parameters
 * @returns {DnaAuthParams} Parsed parameters
 */
export function parseDnaAuthParams(params) {
  return {
    clientId: params.get("client_id"),
    redirectUri: params.get("redirect_uri"),
    state: params.get("state"),
  }
}

/**
 * Validates that a redirect URI points to an allowed Cisco Spaces domain.
 *
 * @param {string} uri - The redirect URI to validate
 * @returns {boolean} True if the URI host matches the allowlist
 */
export function isAllowedRedirectUri(uri) {
  try {
    const url = new URL(uri)
    if (url.protocol !== "https:") return false
    return ALLOWED_REDIRECT_HOSTS.some(
      host => url.hostname === host || url.hostname.endsWith("." + host)
    )
  } catch {
    return false
  }
}

/**
 * DNA Spaces authorization login page.
 * Auto-completes the Cisco Spaces OAuth flow without user interaction.
 *
 * IMPORTANT: This page runs inside the Layout route tree, which means the
 * Landing component has already fired GET /landing and populated the app
 * context (schools, user, etc.) before this page renders. The page MUST
 * wait for `hasData` to be true before attempting the flow — if it fires
 * before Landing completes, `school` will be null and the flow will fail.
 *
 * The route is registered unconditionally (like /oauth/callback) to avoid
 * a race condition during PostHog initialization. The feature flag check
 * is done inside this component instead.
 *
 * Flow:
 * 1. Cisco redirects here with client_id, redirect_uri, state
 * 2. Page waits for Landing to finish loading (hasData === true)
 * 3. Checks feature flag (FeatureFlags.all_integrations)
 * 4. Validates redirect_uri against Cisco Spaces domain allowlist
 * 5. Grabs school from user context (already selected in nav)
 * 6. Calls POST /dna/auth/link with school_id
 * 7. Redirects to Cisco's redirect_uri with code + state
 *
 * @returns {import("react").ReactElement} Loading indicator or error message
 */
export default function DnaAuthLogin() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { school, hasData } = useAppContext()
  const { isSetup, isFeatureEnabled } = useAnalyticsContext()
  const [error, setError] = useState(null)

  useEffect(() => {
    const { clientId, redirectUri, state } = parseDnaAuthParams(searchParams)

    // Validate required params — redirect to home if missing
    if (!clientId || !redirectUri || !state) {
      navigate("/", { replace: true })
      return
    }

    // Validate client_id matches expected CampusIQ integration
    if (clientId !== DNA_SPACES_CLIENT_ID) {
      setError("Invalid client configuration.")
      return
    }

    // Wait for Landing to finish loading before proceeding.
    if (!hasData) {
      return // effect will re-run when hasData changes
    }

    // Wait for PostHog to finish initializing before checking the flag.
    // Without this, isFeatureEnabled returns the default (false) before
    // /decide responds, which would redirect to "/" and lose OAuth params.
    if (!isSetup) {
      return // effect will re-run when isSetup changes
    }

    // Now PostHog is ready — check the actual flag value
    if (!isFeatureEnabled(FeatureFlags.all_integrations)) {
      navigate("/", { replace: true })
      return
    }

    // Validate redirect_uri against known Cisco Spaces domains
    if (!isAllowedRedirectUri(redirectUri)) {
      setError("Invalid redirect destination.")
      return
    }

    // After Landing has loaded, school should be set from user_settings.
    if (!school?.id) {
      setError("No school selected. Please select a school and try again.")
      return
    }

    const controller = new AbortController()

    const completeFlow = async () => {
      try {
        const response = await Api.post("/dna/auth/link", {
          school_id: school.id,
        }, { signal: controller.signal })

        const { code } = response.data.data

        // Build redirect URL with code and state for Cisco
        const redirectUrl = new URL(redirectUri)
        redirectUrl.searchParams.set("code", code)
        redirectUrl.searchParams.set("state", state)

        window.location.href = redirectUrl.toString()
      } catch (e) {
        if (controller.signal.aborted) return
        if (e.handled) return

        console.error("DNA Spaces auth flow failed:", e.error || e)
        setError("Unable to complete DNA Spaces authorization. Please try again.")
      }
    }

    completeFlow()

    return () => controller.abort()
  }, [searchParams, navigate, school, hasData, isSetup, isFeatureEnabled])

  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
        <div className="text-center">
          <p className="text-danger">{error}</p>
          <button type="button" className="btn btn-primary" onClick={() => navigate("/", { replace: true })}>
            Return to Dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
      <div className="text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Connecting to Cisco Spaces...</span>
        </div>
        <p className="mt-2">Connecting to Cisco Spaces...</p>
      </div>
    </div>
  )
}
